import { Component, Input, ChangeDetectionStrategy } from '@angular/core';

import { User } from '../../../../shared/UserInterface';
import { IconComponent } from '../../../../../shared/components/icon/icon.component';
import { IconType } from '../../../../../shared/services/icon.service';
import { UserNameComponent } from '../../../../../shared/components/user-name/user-name.component';

/**
 * Display-only summary of the current user. Edits to the underlying fields
 * (firstName, lastName, displayName, email, …) happen via the dedicated
 * sub-sections below — the header is intentionally read-only.
 */
@Component({
  selector: 'app-profile-header',
  standalone: true,
  imports: [IconComponent, UserNameComponent],
  templateUrl: './profile-header.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './profile-header.component.css',
})
export class ProfileHeaderComponent {
  @Input() user: User | null | undefined;
  @Input() isOAuthUser = false;

  readonly IconType = IconType;

  /**
   * Source for the avatar initials: the same resolved name that
   * `<app-user-name>` renders in public mode, so the initials match the
   * visible label even after a profile refresh.
   */
  private headerName(): string {
    return (
      this.user?.displayName ||
      this.user?.resolvedDisplayName ||
      this.fullName() ||
      this.user?.username ||
      '—'
    );
  }

  private fullName(): string | null {
    if (this.user?.firstName && this.user.lastName) {
      return `${this.user.firstName} ${this.user.lastName}`;
    }
    return this.user?.firstName ?? this.user?.lastName ?? null;
  }

  /**
   * Two-letter initials derived from the same resolved name used in the
   * header so the avatar stays in sync with the visible label.
   */
  getInitials(): string {
    const source = this.headerName();
    if (source === '—') return '?';
    const parts = source.split(' ').filter((part) => part.length > 0);
    if (parts.length >= 2 && parts[0][0] && parts[1][0]) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    if (parts.length >= 1 && parts[0].length >= 2) {
      return parts[0].substring(0, 2).toUpperCase();
    }
    return parts[0]?.[0]?.toUpperCase() || '?';
  }
}
